import { Box } from "@mui/material";
import { useEffect, useRef, useState, useCallback } from "react";
import withAuth from "../../HOC/withAuth";

import kosoImage from "../../Assets/images/koso.png";
import plinthImage from "../../Assets/images/plinth.png";
import runnelImage from "../../Assets/images/runnel.png";
import punchImage from "../../Assets/images/punch.png";
import gesturoImage from "../../Assets/images/gesturo.png";

const projects = [
  {
    name: "Koso",
    description:
      "AI-native IDE for product managers. Collects evidence, synthesizes themes, and generates specs — grounded in your codebase.",
    tag: "PM Tooling",
    image: kosoImage,
    video: "/koso.mp4",
    link: "https://koso-psi.vercel.app/",
  },
  {
    name: "Plinth",
    description:
      "Describe a product idea, get a complete user flow and visual design system. AI-powered design ideation from concept to deliverable.",
    tag: "Design Tool",
    image: plinthImage,
    video: "/plinth.mp4",
    link: "https://plinth-gold.vercel.app/",
  },
  {
    name: "Gesturo",
    description:
      "Draw gestures on canvas, get animation code. Converts natural hand movements into CSS, Framer Motion, GSAP, Lottie, and After Effects.",
    tag: "Animation Tool",
    image: gesturoImage,
    video: "/gesturo.mp4",
    link: "https://gesturo.vercel.app/",
  },
  {
    name: "Punch",
    description:
      "Deform fonts in real-time with physics forces — wind, gravity, magnet, turbulence, pinch, shear. Export as OpenType.",
    tag: "Typography Tool",
    image: punchImage,
    video: "/punch.mp4",
    link: "https://punch-brown.vercel.app/",
  },
  {
    name: "Runnel",
    description:
      "Describe automations in plain English. Connects Gmail, Slack, Sheets, Notion, Calendar, and Stripe — no flowchart builder needed.",
    tag: "Automation",
    image: runnelImage,
    video: "/runnel.mp4",
    link: "https://runnel.up.railway.app/",
  },
];

const AIBuilt = () => {
  const [modal, setModal] = useState<{
    video: string;
    poster: string;
  } | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [badgeText, setBadgeText] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevIndexRef = useRef(-1);

  // Typewriter effect for badge — starts after 2s delay
  useEffect(() => {
    const fullText = "built with claude code";
    let i = 0;
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setBadgeText(fullText.slice(0, i));
        if (i >= fullText.length) clearInterval(interval);
      }, 50);
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track which card is most centered in viewport
  useEffect(() => {
    const onScroll = () => {
      const viewportCenter = window.innerHeight / 2;
      let closest = 0;
      let closestDist = Infinity;

      mediaRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cardCenter = rect.top + rect.height / 2;
        const dist = Math.abs(cardCenter - viewportCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });

      setActiveIndex(closest);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // Small delay to ensure refs are populated
    const timer = setTimeout(onScroll, 100);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  // Move the single video element into the active card's media container
  useEffect(() => {
    if (activeIndex < 0) return;
    const video = videoRef.current;
    const target = mediaRefs.current[activeIndex];
    if (!video || !target) return;

    // Skip if already in this card
    if (prevIndexRef.current === activeIndex && video.parentElement === target) return;
    prevIndexRef.current = activeIndex;

    target.appendChild(video);
    video.style.opacity = "1";
    video.style.position = "absolute";
    video.style.top = "0";
    video.style.left = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "1";
    video.src = projects[activeIndex].video;
    video.load();
    video.play().catch(() => {});
  }, [activeIndex]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (modal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [modal]);

  const handleExpand = useCallback(
    (video: string, poster: string) => setModal({ video, poster }),
    []
  );

  return (
    <Box sx={{ minHeight: "100vh", overflow: "hidden" }}>
      <style>
        {`
          .ai-page {
            max-width: 1200px;
            margin: 0 auto;
            padding: 140px 60px 100px;
          }

          .ai-header {
            margin-bottom: 80px;
          }

          .ai-title {
            font-size: clamp(48px, 7vw, 80px);
            font-weight: 900;
            font-style: italic;
            text-transform: uppercase;
            letter-spacing: -3px;
            line-height: 0.95;
            color: #000;
            margin-bottom: 24px;
          }

          .ai-title span {
            color: #0021F5;
          }

          .ai-subtitle {
            font-size: 18px;
            font-weight: 500;
            color: #555;
            max-width: 600px;
            line-height: 1.6;
          }

          .ai-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: #000;
            color: #ADFF00;
            font-size: 13px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            margin-bottom: 20px;
            font-family: 'DM Mono', 'SF Mono', 'Fira Code', monospace;
          }

          .ai-badge::before {
            content: '>';
            opacity: 0.5;
          }

          .ai-badge__cursor {
            animation: blink 0.7s step-end infinite;
            margin-left: 1px;
            font-weight: 400;
          }

          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }

          .ai-scanline {
            display: none;
          }

          .ai-card {
            display: flex;
            border: 4px solid #000;
            background: #fff;
            margin-bottom: 50px;
            cursor: pointer;
            transition: transform 0.25s ease, box-shadow 0.25s ease;
            box-shadow: 8px 8px 0 #000;
            position: relative;
          }

          .ai-card:hover {
            transform: translate(-4px, -4px);
            box-shadow: 12px 12px 0 #0021F5;
          }

          .ai-card:hover .ai-scanline { opacity: 1; }
          .ai-card:hover .ai-card__index { color: #ADFF00; background: #000; }
          .ai-card:hover .ai-card__expand { opacity: 1; }

          .ai-card:nth-child(even) {
            flex-direction: row-reverse;
          }

          .ai-card__media {
            position: relative;
            background: #f5f5f5;
            flex: 0 0 55%;
            overflow: hidden;
            line-height: 0;
          }

          .ai-card__poster {
            width: 100%;
            display: block;
          }

          /* The single shared video — positioned over the poster */
          .ai-shared-video {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 1;
          }

          .ai-card__expand {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 3;
            width: 36px;
            height: 36px;
            background: rgba(0, 0, 0, 0.7);
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 0;
            color: #fff;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.2s ease, background 0.2s ease;
          }

          .ai-card__expand:hover {
            background: #0021F5;
            border-color: #0021F5;
          }

          .ai-card__info {
            padding: 24px 30px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            position: relative;
            flex: 1;
            overflow: hidden;
          }

          .ai-card__index {
            position: absolute;
            top: 10px;
            right: 10px;
            font-size: 12px;
            font-weight: 900;
            font-family: 'DM Mono', 'SF Mono', monospace;
            color: #ccc;
            padding: 3px 8px;
            border: 2px solid currentColor;
            transition: all 0.3s ease;
          }

          .ai-card__tag {
            display: inline-block;
            padding: 4px 10px;
            background: #0021F5;
            color: #fff;
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            margin-bottom: 10px;
            width: fit-content;
          }

          .ai-card__name {
            font-size: 26px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: -0.5px;
            margin-bottom: 8px;
            color: #000;
          }

          .ai-card__desc {
            font-size: 14px;
            line-height: 1.5;
            color: #444;
          }

          .ai-card__tech {
            margin-top: 12px;
            font-size: 11px;
            font-family: 'DM Mono', 'SF Mono', monospace;
            color: #999;
            letter-spacing: 0.5px;
          }

          .ai-card__tech span {
            color: #ADFF00;
            background: #000;
            padding: 2px 8px;
            margin-right: 6px;
            font-weight: 700;
          }

          .ai-card__link {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            margin-top: 14px;
            color: #0021F5;
            font-size: 14px;
            font-weight: 700;
            text-decoration: none;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            transition: gap 0.3s ease;
          }

          .ai-card__link:hover { gap: 15px; }

          .ai-card__link .arrow {
            display: inline-block;
            width: 14px;
            height: 14px;
            border-top: 2.5px solid currentColor;
            border-right: 2.5px solid currentColor;
            transform: rotate(45deg);
          }

          /* Modal */
          .ai-modal {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            z-index: 99999;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: modalFadeIn 0.25s ease;
          }

          @keyframes modalFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .ai-modal__backdrop {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0, 0, 0, 0.9);
            cursor: pointer;
          }

          .ai-modal__content {
            position: relative;
            max-width: 90vw;
            max-height: 90vh;
            z-index: 1;
          }

          .ai-modal__content video {
            max-width: 90vw;
            max-height: 90vh;
            display: block;
            border: 4px solid #fff;
          }

          .ai-modal__close {
            position: fixed;
            top: 20px;
            right: 20px;
            width: 36px;
            height: 36px;
            background: #fff;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 900;
            color: #000;
            z-index: 2;
          }

          .ai-modal__close:hover { background: #ADFF00; }

          @media (max-width: 768px) {
            .ai-page { padding: 120px 20px 60px; }
            .ai-title { font-size: clamp(32px, 10vw, 52px); letter-spacing: -2px; }
            .ai-card { flex-direction: column; margin-bottom: 35px; box-shadow: 5px 5px 0 #000; }
            .ai-card:nth-child(even) { flex-direction: column; }
            .ai-card__media { flex: none; }
            .ai-card__expand { opacity: 1; }
            .ai-card:hover { transform: translate(-2px, -2px); box-shadow: 7px 7px 0 #0021F5; }
            .ai-card__info { padding: 28px 22px; }
            .ai-card__name { font-size: 24px; }
            .ai-card__desc { font-size: 15px; }
          }

          @media (max-width: 480px) {
            .ai-page { padding: 100px 15px 40px; }
            .ai-header { margin-bottom: 50px; }
            .ai-card__info { padding: 22px 18px; }
            .ai-card__name { font-size: 20px; }
          }
        `}
      </style>

      {/* Single shared video element — gets moved between cards */}
      <video
        ref={videoRef}
        className="ai-shared-video"
        muted
        loop
        playsInline
        style={{ position: "absolute", opacity: 0 }}
      />

      <div className="ai-page">
        <div className="ai-header">
          <div className="ai-badge">{badgeText}<span className="ai-badge__cursor">|</span></div>
          <h1 className="ai-title">
            Designed + Shipped <span>with AI</span>
          </h1>
          <p className="ai-subtitle">
            Always experimenting with new AI workflows. These are products I
            designed and shipped end-to-end — using AI not just for code, but
            as a thinking and making tool throughout.
          </p>
        </div>

        {projects.map((project, index) => (
          <div
            className="ai-card"
            key={project.name}
            onClick={() =>
              window.open(project.link, "_blank", "noopener,noreferrer")
            }
          >
            <div
              className="ai-card__media"
              ref={(el) => { mediaRefs.current[index] = el; }}
            >
              <img
                src={project.image}
                alt={project.name}
                className="ai-card__poster"
              />
              <button
                className="ai-card__expand"
                title="Expand video"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpand(project.video, project.image);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <polyline points="10,2 14,2 14,6" />
                  <polyline points="6,14 2,14 2,10" />
                  <line x1="14" y1="2" x2="9" y2="7" />
                  <line x1="2" y1="14" x2="7" y2="9" />
                </svg>
              </button>
              <div className="ai-scanline" />
            </div>
            <div className="ai-card__info">
              <div className="ai-card__index">
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="ai-card__tag">{project.tag}</div>
              <h2 className="ai-card__name">{project.name}</h2>
              <p className="ai-card__desc">{project.description}</p>
              <div className="ai-card__tech">
                <span>AI</span> designed + shipped with Claude Code
              </div>
              <a
                className="ai-card__link"
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                View Product <span className="arrow"></span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="ai-modal">
          <div
            className="ai-modal__backdrop"
            onClick={() => setModal(null)}
          />
          <div className="ai-modal__content">
            <button
              className="ai-modal__close"
              onClick={() => setModal(null)}
            >
              &times;
            </button>
            <video
              src={modal.video}
              poster={modal.poster}
              autoPlay
              muted
              loop
              playsInline
            />
          </div>
        </div>
      )}
    </Box>
  );
};

export default withAuth(AIBuilt);
